import type { OrderStage } from '@xshop/shared';

// What the order lookup says, in each language a store is served in: the two fields, what went wrong, and
// how far a found order has come.

/** Every phrase the order lookup and the order it finds are drawn with. */
export type OrderLookupWords = {
  orderNumber: string;
  phone: string;
  find: string;
  finding: string;
  hint: string;
  missing: string;
  failed: string;
  tooMany: string;
  stages: Readonly<Record<OrderStage, string>>;
  order: (number: string) => string;
  placedOn: (date: string) => string;
  deliveringTo: (city: string) => string;
  cancelled: string;
  tracking: string;
  trackParcel: string;
  quantity: (count: number) => string;
  subtotal: string;
  shipping: string;
  free: string;
  discount: string;
  total: string;
  another: string;
};

/** The words in English, French and Arabic. */
export const ORDER_LOOKUP_WORDS: Readonly<Record<'en' | 'fr' | 'ar', OrderLookupWords>> = {
  en: {
    orderNumber: 'Order number', phone: 'Phone', find: 'Find my order', finding: 'Looking…',
    hint: 'Both are on your order confirmation.',
    missing: 'No order matches that number and phone. Check both, then try again.',
    failed: 'We could not look up your order just now. Please try again.',
    tooMany: 'Too many tries. Please wait a few minutes, then try again.',
    stages: { placed: 'Order placed', shipped: 'On its way', delivered: 'Delivered', cancelled: 'Cancelled' },
    order: (number) => `Order ${number}`, placedOn: (date) => `Placed on ${date}`, deliveringTo: (city) => `Delivering to ${city}`,
    cancelled: 'This order was cancelled. Contact us if you did not ask for that.',
    tracking: 'Tracking number', trackParcel: 'Track your parcel', quantity: (count) => `Qty ${count}`,
    subtotal: 'Subtotal', shipping: 'Shipping', free: 'Free', discount: 'Discount', total: 'Total', another: 'Look up another order'
  },
  fr: {
    orderNumber: 'Numéro de commande', phone: 'Téléphone', find: 'Trouver ma commande', finding: 'Recherche…',
    hint: 'Les deux figurent sur votre confirmation de commande.',
    missing: 'Aucune commande ne correspond à ce numéro et ce téléphone. Vérifiez-les, puis réessayez.',
    failed: 'Impossible de retrouver votre commande pour le moment. Veuillez réessayer.',
    tooMany: 'Trop d’essais. Patientez quelques minutes, puis réessayez.',
    stages: { placed: 'Commande passée', shipped: 'En route', delivered: 'Livrée', cancelled: 'Annulée' },
    order: (number) => `Commande ${number}`, placedOn: (date) => `Passée le ${date}`, deliveringTo: (city) => `Livraison à ${city}`,
    cancelled: 'Cette commande a été annulée. Contactez-nous si vous ne l’avez pas demandé.',
    tracking: 'Numéro de suivi', trackParcel: 'Suivre mon colis', quantity: (count) => `Qté ${count}`,
    subtotal: 'Sous-total', shipping: 'Livraison', free: 'Gratuite', discount: 'Remise', total: 'Total', another: 'Rechercher une autre commande'
  },
  ar: {
    orderNumber: 'رقم الطلب', phone: 'رقم الهاتف', find: 'ابحث عن طلبي', finding: 'جارٍ البحث…',
    hint: 'تجدهما في تأكيد طلبك.',
    missing: 'لا يوجد طلب بهذا الرقم وهذا الهاتف. تحقق منهما ثم أعد المحاولة.',
    failed: 'تعذر العثور على طلبك الآن. يرجى إعادة المحاولة.',
    tooMany: 'محاولات كثيرة. انتظر بضع دقائق ثم أعد المحاولة.',
    stages: { placed: 'تم الطلب', shipped: 'في الطريق', delivered: 'تم التسليم', cancelled: 'ملغى' },
    order: (number) => `الطلب ${number}`, placedOn: (date) => `بتاريخ ${date}`, deliveringTo: (city) => `التوصيل إلى ${city}`,
    cancelled: 'تم إلغاء هذا الطلب. تواصل معنا إذا لم تطلب ذلك.',
    tracking: 'رقم التتبع', trackParcel: 'تتبع طردك', quantity: (count) => `الكمية ${count}`,
    subtotal: 'المجموع الفرعي', shipping: 'التوصيل', free: 'مجاني', discount: 'الخصم', total: 'المجموع', another: 'ابحث عن طلب آخر'
  }
};

/** The locale each store language writes a date in. */
export const DATE_LOCALES: Readonly<Record<'en' | 'fr' | 'ar', string>> = { en: 'en-GB', fr: 'fr-MA', ar: 'ar-MA' };
